import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchWeeklyProductivity } from '../../operations/jiraService';
import { WeeklyProductivityData, StaffRow, WeekLabel, WeekData } from './types';

// ======================================================================
//  INTERFACES
// ======================================================================

interface StaffProductivityProps {
  globalProject?: string;
}

interface ScoreColorResult {
  text: string;
  bg: string;
  border: string;
  fill: string;
}

interface AvatarProps {
  name: string;
  size?: string;
}

interface ScoreRingProps {
  score: number;
  size?: number;
}

interface MultiSelectProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  allLabel?: string;
}

interface FunnelChartProps {
  staffRows: StaffRow[];
  weeks: WeekLabel[];
}

interface ScoreTrendChartProps {
  staffRows: StaffRow[];
  weeks: WeekLabel[];
  selectedStaff: string[];
}

interface ContributionEntry {
  devShare: number;
  uatShare: number;
  prodShare: number;
  totalShare: number;
  efficiency: number;
  contribution: number;
  finalScore: number;
}

interface Insights {
  topPerformer: { name: string; score: number; prodShare: number; efficiency: number } | null;
  mostConsistent: { name: string; avgScore: number } | null;
  biggestImprover: { name: string; increase: number } | null;
  bottleneck: { name: string; closureRate: number; prodRate: number } | null;
  teamAvgScore: number;
}

interface TeamEnvTotals {
  assigned: number;
  closed: number;
  DEV: number;
  UAT: number;
  PROD: number;
}

// ======================================================================
//  CONSTANTS
// ======================================================================

const EXCLUDED_STAFF: string[] = [
  "faiyaz ali syed", "chandani doshi", "kirill", "vipul", "suren", "harmeet", "poonam", "pritam",
];

function isExcludedStaff(name: string): boolean {
  const n = (name || "").toLowerCase();
  return EXCLUDED_STAFF.some(ex => n.includes(ex));
}

const STAGE_COLORS: Record<string, string> = { DEV: "#00b8ff", UAT: "#FBBF24", PROD: "#7BC410" };
const LINE_COLORS: string[] = [
  "#6c00f6", "#00b8ff", "#a1f919", "#FBBF24", "#EF4444",
  "#22D3EE", "#F97316", "#8B33FF", "#10B981", "#EC4899",
  "#3B82F6", "#F59E0B", "#14B8A6", "#E879F9", "#84CC16",
];

// ======================================================================
//  UTILITIES
// ======================================================================

function initials(name: string): string {
  const p = (name || "?").split(" ");
  return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : p[0][0].toUpperCase();
}

function Avatar({ name, size = "w-7 h-7 text-[10px]" }: AvatarProps) {
  return (
    <div className={`${size} rounded-full bg-gradient-to-br from-zen-purple to-zen-cyan flex items-center justify-center font-bold text-white shrink-0`}>
      {initials(name)}
    </div>
  );
}

function scoreColor(score: number): ScoreColorResult {
  if (score >= 70) return { text: "text-emerald-500", bg: "bg-emerald-50", border: "border-emerald-200", fill: "#10B981" };
  if (score >= 40) return { text: "text-amber-500", bg: "bg-amber-50", border: "border-amber-200", fill: "#F59E0B" };
  return { text: "text-red-500", bg: "bg-red-50", border: "border-red-200", fill: "#EF4444" };
}

function scoreLabel(score: number): string {
  if (score >= 80) return "Excellent";
  if (score >= 70) return "Strong";
  if (score >= 50) return "Average";
  if (score >= 30) return "Below Avg";
  return "Low";
}

function weekDateRange(week: WeekLabel): string {
  if (!week.start || !week.end) return week.label;
  const s = new Date(week.start);
  const e = new Date(week.end);
  const fmt = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${week.label} \u00b7 ${fmt(s)}\u2013${fmt(e)}`;
}

// ======================================================================
//  MULTI-SELECT DROPDOWN
// ======================================================================

function MultiSelect({ label, options, selected, onChange, allLabel = "All" }: MultiSelectProps) {
  const [open, setOpen] = useState<boolean>(false);
  const isAll = selected.length === 0 || selected.length === options.length;
  const displayText = isAll ? allLabel : selected.length === 1 ? selected[0] : `${selected.length} selected`;

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 bg-surface-3 border border-edge-1 rounded-lg px-3 py-1.5 text-xs font-semibold text-txt-2 hover:border-zen-purple transition-colors min-w-[120px]">
        <span className="text-[10px] text-txt-4 uppercase tracking-wider">{label}</span>
        <span className="ml-auto truncate max-w-[100px]">{displayText}</span>
        <svg className={`w-3 h-3 text-txt-4 transition-transform ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-0 mt-1 bg-surface-2 border border-edge-1 rounded-lg shadow-lg z-30 min-w-[180px] max-h-[240px] overflow-y-auto py-1">
            <button onClick={() => { onChange([]); }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-surface-3 transition-colors ${isAll ? "text-zen-purple font-bold" : "text-txt-2"}`}>
              {allLabel}
            </button>
            {options.map((opt: string) => {
              const active = selected.includes(opt);
              return (
                <button key={opt} onClick={() => {
                  const next = active ? selected.filter((s: string) => s !== opt) : [...selected, opt];
                  onChange(next.length === options.length ? [] : next);
                }}
                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-surface-3 transition-colors flex items-center gap-2 ${active ? "text-zen-purple font-semibold" : "text-txt-2"}`}>
                  <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${active ? "bg-zen-purple border-zen-purple" : "border-edge-2"}`}>
                    {active && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path d="M5 13l4 4L19 7" /></svg>}
                  </span>
                  {opt}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ======================================================================
//  SCORE RING (mini donut for score)
// ======================================================================

function ScoreRing({ score, size = 44 }: ScoreRingProps) {
  const r = size / 2 - 4;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - Math.min(score, 100) / 100);
  const sc = scoreColor(score);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E5E7EB" strokeWidth={3.5} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={sc.fill} strokeWidth={3.5}
        strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text x={size / 2} y={size / 2 + 4} textAnchor="middle" fontSize={11} fontWeight={700} fill={sc.fill} fontFamily="JetBrains Mono">
        {score}
      </text>
    </svg>
  );
}

// ======================================================================
//  ENVIRONMENT FUNNEL BAR
// ======================================================================

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function FunnelChart({ staffRows, weeks }: FunnelChartProps) {
  if (!staffRows || staffRows.length === 0) return null;
  const barH = 26, gap = 5, padL = 130, padR = 40, padT = 8, padB = 28;
  const chartW = 700;
  const chartH = padT + staffRows.length * (barH + gap) + padB;
  const maxVal = Math.max(1, ...staffRows.map((r: StaffRow) => r.totals.closed));
  const barArea = chartW - padL - padR;

  return (
    <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full" style={{ minHeight: Math.max(180, chartH) }}>
      {staffRows.map((row: StaffRow, i: number) => {
        const y = padT + i * (barH + gap);
        let x = padL;
        return (
          <g key={row.name}>
            <text x={padL - 8} y={y + barH / 2 + 4} textAnchor="end" fontSize={10} fill="#4B5563" fontFamily="DM Sans" fontWeight={600}>
              {row.name.length > 16 ? row.name.slice(0, 15) + "\u2026" : row.name}
            </text>
            <rect x={padL} y={y} width={barArea} height={barH} rx={4} fill="#F1F3F6" />
            {(["DEV", "UAT", "PROD"] as const).map((stage) => {
              const val = row.totals[stage];
              if (val === 0) return null;
              const w = Math.max((val / maxVal) * barArea, 2);
              const sx = x;
              x += w;
              const ticketList = weeks.reduce((acc: string[], wk: WeekLabel) => {
                const wd = row.weeks[wk.label] as WeekData | undefined;
                if (wd?.tickets?.[stage]) acc.push(...wd.tickets[stage]);
                return acc;
              }, []);
              return (
                <g key={stage}>
                  <rect x={sx} y={y} width={w} height={barH} rx={sx === padL ? 4 : 0}
                    fill={STAGE_COLORS[stage]} opacity={0.85}>
                    <title>{`${row.name} \u2014 ${stage}: ${val} tickets\n${ticketList.join(", ")}`}</title>
                  </rect>
                  {w > 20 && (
                    <text x={sx + w / 2} y={y + barH / 2 + 4} textAnchor="middle"
                      fontSize={9} fontWeight={700} fill="#fff" fontFamily="JetBrains Mono">{val}</text>
                  )}
                </g>
              );
            })}
            {/* Score badge at end */}
            <text x={x + 8} y={y + barH / 2 + 4} fontSize={10} fontWeight={700} fill={scoreColor(row.totals.score).fill} fontFamily="JetBrains Mono">
              {row.totals.score}
            </text>
          </g>
        );
      })}
      {(["DEV", "UAT", "PROD"] as const).map((s: string, i: number) => (
        <g key={s} transform={`translate(${padL + i * 90}, ${chartH - 6})`}>
          <rect width={10} height={10} rx={2} fill={STAGE_COLORS[s]} opacity={0.85} />
          <text x={14} y={9} fontSize={9} fill="#6B7280" fontFamily="DM Sans" fontWeight={600}>{s}</text>
        </g>
      ))}
    </svg>
  );
}

// ======================================================================
//  SCORE TREND LINE CHART
// ======================================================================

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ScoreTrendChart({ staffRows, weeks, selectedStaff }: ScoreTrendChartProps) {
  if (!weeks || weeks.length === 0) return null;
  const padL = 44, padR = 20, padT = 20, padB = 44;
  const W = 700, H = 260;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const staffCount = staffRows.length || 1;

  const visibleStaff = selectedStaff.length > 0
    ? staffRows.filter((r: StaffRow) => selectedStaff.includes(r.name))
    : staffRows;

  const avgValues = weeks.map((w: WeekLabel) => {
    const sum = staffRows.reduce((s: number, r: StaffRow) => s + (r.weeks[w.label]?.score || 0), 0);
    return Math.round(sum / staffCount);
  });

  const maxVal = 100; // Score is 0-100
  const xStep = weeks.length > 1 ? plotW / (weeks.length - 1) : plotW;
  const toX = (i: number) => padL + (weeks.length > 1 ? i * xStep : plotW / 2);
  const toY = (v: number) => padT + plotH - (v / maxVal) * plotH;

  const gridVals = [0, 25, 50, 75, 100];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      {gridVals.map((v: number, i: number) => (
        <g key={i}>
          <line x1={padL} y1={toY(v)} x2={W - padR} y2={toY(v)} stroke="#E5E7EB" strokeWidth={0.5} />
          <text x={padL - 6} y={toY(v) + 3} textAnchor="end" fontSize={9} fill="#9CA3AF" fontFamily="JetBrains Mono">{v}</text>
        </g>
      ))}

      {/* Score zone bands */}
      <rect x={padL} y={toY(100)} width={plotW} height={toY(70) - toY(100)} fill="#10B981" opacity={0.04} />
      <rect x={padL} y={toY(70)} width={plotW} height={toY(40) - toY(70)} fill="#F59E0B" opacity={0.04} />
      <rect x={padL} y={toY(40)} width={plotW} height={toY(0) - toY(40)} fill="#EF4444" opacity={0.04} />

      {weeks.map((w: WeekLabel, i: number) => (
        <text key={w.label} x={toX(i)} y={H - padB + 16} textAnchor="middle" fontSize={10} fill="#6B7280" fontFamily="DM Sans" fontWeight={600}>
          {w.label}
        </text>
      ))}

      {visibleStaff.map((row: StaffRow, ri: number) => {
        const color = LINE_COLORS[ri % LINE_COLORS.length];
        const points = weeks.map((w: WeekLabel, i: number) => `${toX(i)},${toY(row.weeks[w.label]?.score || 0)}`).join(" ");
        return (
          <g key={row.name}>
            <polyline points={points} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
            {weeks.map((w: WeekLabel, i: number) => (
              <circle key={i} cx={toX(i)} cy={toY(row.weeks[w.label]?.score || 0)} r={3.5} fill={color}>
                <title>{`${row.name} \u2014 ${w.label}: Score ${row.weeks[w.label]?.score || 0} (Closure: ${row.weeks[w.label]?.closureRate || 0}%, PROD: ${row.weeks[w.label]?.prodRate || 0}%)`}</title>
              </circle>
            ))}
          </g>
        );
      })}

      {/* Team average (dashed) */}
      <polyline
        points={weeks.map((_: WeekLabel, i: number) => `${toX(i)},${toY(avgValues[i])}`).join(" ")}
        fill="none" stroke="#9CA3AF" strokeWidth={1.5} strokeDasharray="6 3" strokeLinejoin="round"
      />

      {/* Legend */}
      {visibleStaff.slice(0, 8).map((row: StaffRow, i: number) => {
        const color = LINE_COLORS[i % LINE_COLORS.length];
        const lx = padL + (i % 4) * 160;
        const ly = H - 8 + Math.floor(i / 4) * 14;
        return (
          <g key={row.name} transform={`translate(${lx}, ${ly})`}>
            <line x1={0} y1={0} x2={12} y2={0} stroke={color} strokeWidth={2} />
            <text x={16} y={3} fontSize={9} fill="#4B5563" fontFamily="DM Sans">{row.name.split(" ")[0]}</text>
          </g>
        );
      })}
      <g transform={`translate(${padL + (Math.min(visibleStaff.length, 4)) * 160}, ${H - 8})`}>
        <line x1={0} y1={0} x2={12} y2={0} stroke="#9CA3AF" strokeWidth={1.5} strokeDasharray="4 2" />
        <text x={16} y={3} fontSize={9} fill="#9CA3AF" fontFamily="DM Sans">Team Avg</text>
      </g>
    </svg>
  );
}

// ======================================================================
//  CSV EXPORT
// ======================================================================

function exportCSV(staffRows: StaffRow[], weeks: WeekLabel[]): void {
  const headers: string[] = [
    "Staff",
    ...weeks.flatMap((w: WeekLabel) => [`${w.label} Assigned`, `${w.label} Closed`, `${w.label} PROD`, `${w.label} Closure%`, `${w.label} PROD%`, `${w.label} Score`]),
    "Overall Score", "Overall Closure%", "Overall PROD%",
  ];
  const rows = staffRows.map((row: StaffRow) => [
    row.name,
    ...weeks.flatMap((w: WeekLabel) => {
      const d: WeekData = row.weeks[w.label] || {} as WeekData;
      return [d.assigned || 0, d.closed || 0, d.PROD || 0, d.closureRate || 0, d.prodRate || 0, d.score || 0];
    }),
    row.totals.score, row.totals.closureRate, row.totals.prodRate,
  ]);
  const csv = [headers.join(","), ...rows.map((r: (string | number)[]) => r.map((c: string | number) => `"${c}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `productivity-scores-${new Date().toISOString().split("T")[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ======================================================================
//  MAIN COMPONENT
// ======================================================================

export default function StaffProductivity({ globalProject }: StaffProductivityProps) {
  const [data, setData] = useState<WeeklyProductivityData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [err, setErr] = useState<string | null>(null);

  const [staffFilter, setStaffFilter] = useState<string[]>([]);
  const [weeksBack, setWeeksBack] = useState<number>(4);
  const [projectFilter, setProjectFilter] = useState<string>("ALL");
  const [sortCol, setSortCol] = useState<string>("score");
  const [sortDir, setSortDir] = useState<string>("desc");

  useEffect(() => {
    if (globalProject) setProjectFilter(globalProject);
  }, [globalProject]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setErr(null);
      const d = await fetchWeeklyProductivity(weeksBack, projectFilter);
      setData(d);
    } catch (error: unknown) {
      setErr(error instanceof Error ? error.message : String(error));
    } finally {
      setLoading(false);
    }
  }, [weeksBack, projectFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetFilters = useCallback(() => {
    setStaffFilter([]);
    setWeeksBack(4);
    setProjectFilter(globalProject || "ALL");
    setSortCol("score");
    setSortDir("desc");
  }, [globalProject]);

  // -- Team environment totals + contribution-based scores --
  // Computed from raw data (before filtering/sorting) to avoid circular dependency
  const { teamEnvTotals, contributionMap } = useMemo<{ teamEnvTotals: TeamEnvTotals; contributionMap: Record<string, ContributionEntry> }>(() => {
    if (!data) return { teamEnvTotals: { assigned: 0, closed: 0, DEV: 0, UAT: 0, PROD: 0 }, contributionMap: {} };
    const allRows = data.staffRows.filter((r: StaffRow) => !isExcludedStaff(r.name));

    const t: TeamEnvTotals = { assigned: 0, closed: 0, DEV: 0, UAT: 0, PROD: 0 };
    allRows.forEach((r: StaffRow) => {
      t.assigned += r.totals.assigned || 0;
      t.closed += r.totals.closed || 0;
      t.DEV += r.totals.DEV || 0;
      t.UAT += r.totals.UAT || 0;
      t.PROD += r.totals.PROD || 0;
    });

    const totalDeployments = t.DEV + t.UAT + t.PROD;
    const map: Record<string, ContributionEntry> = {};
    allRows.forEach((r: StaffRow) => {
      const devShare = totalDeployments > 0 ? Math.round(((r.totals.DEV || 0) / totalDeployments) * 100) : 0;
      const uatShare = totalDeployments > 0 ? Math.round(((r.totals.UAT || 0) / totalDeployments) * 100) : 0;
      const prodShare = totalDeployments > 0 ? Math.round(((r.totals.PROD || 0) / totalDeployments) * 100) : 0;
      const totalShare = devShare + uatShare + prodShare;
      const efficiency = r.totals.score;
      const contribution = Math.round((devShare * 0.15) + (uatShare * 0.25) + (prodShare * 0.60));
      const finalScore = Math.min(100, Math.round((efficiency * 0.3) + (contribution * 0.7)));
      map[r.name] = { devShare, uatShare, prodShare, totalShare, efficiency, contribution, finalScore };
    });

    return { teamEnvTotals: t, contributionMap: map };
  }, [data]);

  // -- Filtered + Sorted data --
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { filteredRows, weeks, teamTotals, weeklyAvg, allStaffNames } = useMemo<{
    filteredRows: StaffRow[];
    weeks: WeekLabel[];
    teamTotals: Record<string, WeekData>;
    weeklyAvg: Record<string, number>;
    allStaffNames: string[];
  }>(() => {
    if (!data) return { filteredRows: [], weeks: [], teamTotals: {}, weeklyAvg: {}, allStaffNames: [] };
    const allNames = data.staffRows.filter((r: StaffRow) => !isExcludedStaff(r.name)).map((r: StaffRow) => r.name);
    let rows = data.staffRows.filter((r: StaffRow) => !isExcludedStaff(r.name));

    if (staffFilter.length > 0) {
      rows = rows.filter((r: StaffRow) => staffFilter.includes(r.name));
    }

    if (sortCol) {
      rows = [...rows].sort((a: StaffRow, b: StaffRow) => {
        let av: string | number, bv: string | number;
        if (sortCol === "name") { av = a.name; bv = b.name; }
        else if (sortCol === "score") { av = contributionMap[a.name]?.finalScore ?? a.totals.score; bv = contributionMap[b.name]?.finalScore ?? b.totals.score; }
        else if (sortCol === "closureRate") { av = a.totals.closureRate; bv = b.totals.closureRate; }
        else if (sortCol === "prodRate") { av = a.totals.prodRate; bv = b.totals.prodRate; }
        else if (sortCol === "assigned") { av = a.totals.assigned; bv = b.totals.assigned; }
        else {
          av = a.weeks[sortCol]?.score || 0;
          bv = b.weeks[sortCol]?.score || 0;
        }
        if (typeof av === "string" && typeof bv === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
        return sortDir === "asc" ? (av as number) - (bv as number) : (bv as number) - (av as number);
      });
    }

    return {
      filteredRows: rows,
      weeks: data.weeks || [],
      teamTotals: data.teamWeekTotals || {},
      weeklyAvg: data.weeklyAverages || {},
      allStaffNames: allNames,
    };
  }, [data, staffFilter, sortCol, sortDir, contributionMap]);

  const handleSort = useCallback((col: string) => {
    setSortCol((prev: string) => {
      if (prev === col) { setSortDir((d: string) => d === "asc" ? "desc" : "asc"); return col; }
      setSortDir("desc");
      return col;
    });
  }, []);

  // -- Summary insights --
  const insights = useMemo<Insights>(() => {
    if (!data || filteredRows.length === 0) return { topPerformer: null, mostConsistent: null, biggestImprover: null, bottleneck: null, teamAvgScore: 0 };

    const teamAvgScore = filteredRows.length > 0
      ? Math.round(filteredRows.reduce((s: number, r: StaffRow) => s + (contributionMap[r.name]?.finalScore || 0), 0) / filteredRows.length)
      : 0;

    // Top performer: highest final score (contribution-weighted)
    let topPerformer: Insights['topPerformer'] = null;
    for (const row of filteredRows) {
      const cm = contributionMap[row.name] || {} as ContributionEntry;
      if (!topPerformer || (cm.finalScore || 0) > topPerformer.score) {
        topPerformer = { name: row.name, score: cm.finalScore || 0, prodShare: cm.prodShare || 0, efficiency: cm.efficiency || 0 };
      }
    }

    // Most consistent: lowest score variance
    let mostConsistent: Insights['mostConsistent'] = null;
    let lowestVar = Infinity;
    for (const row of filteredRows) {
      const scores = weeks.map((w: WeekLabel) => row.weeks[w.label]?.score || 0);
      const mean = scores.reduce((s: number, v: number) => s + v, 0) / (scores.length || 1);
      if (mean === 0) continue;
      const variance = scores.reduce((s: number, v: number) => s + Math.pow(v - mean, 2), 0) / (scores.length || 1);
      if (variance < lowestVar) { lowestVar = variance; mostConsistent = { name: row.name, avgScore: Math.round(mean) }; }
    }

    // Biggest improver: highest score increase between last two weeks
    let biggestImprover: Insights['biggestImprover'] = null;
    if (weeks.length >= 2) {
      const w1 = weeks[weeks.length - 2].label;
      const w2 = weeks[weeks.length - 1].label;
      let maxInc = -Infinity;
      for (const row of filteredRows) {
        const inc = (row.weeks[w2]?.score || 0) - (row.weeks[w1]?.score || 0);
        if (inc > maxInc) { maxInc = inc; biggestImprover = { name: row.name, increase: inc }; }
      }
    }

    // Bottleneck: high closure rate but low prod rate
    let bottleneck: Insights['bottleneck'] = null;
    for (const row of filteredRows) {
      if (row.totals.closureRate >= 50 && row.totals.prodRate < 25 && row.totals.closed > 2) {
        bottleneck = { name: row.name, closureRate: row.totals.closureRate, prodRate: row.totals.prodRate };
        break;
      }
    }

    return { topPerformer, mostConsistent, biggestImprover, bottleneck, teamAvgScore };
  }, [data, filteredRows, weeks, contributionMap]);

  // -- Loading / Error --
  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-8 h-8 border-[3px] border-edge-2 border-t-zen-purple rounded-full animate-spin mx-auto mb-3" />
          <p className="text-txt-3 text-xs">Loading productivity scores...</p>
        </div>
      </div>
    );
  }

  if (err && !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <p className="text-red-400 font-semibold text-sm mb-2">Failed to load</p>
          <p className="text-txt-3 text-xs mb-3">{err}</p>
          <button onClick={fetchData} className="px-3 py-1.5 rounded-lg bg-zen-purple text-white text-xs font-semibold">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* ================================================================ */}
      {/* FORMULA EXPLAINER + TEAM SCORE                                   */}
      {/* ================================================================ */}
      <div className="bg-surface-2 border border-edge-1 rounded-xl p-5 shadow-sm">
        <div className="flex items-start justify-between gap-6">
          <div className="flex-1">
            <h3 className="text-base font-bold text-txt-1 mb-2">Productivity Score</h3>
            <div className="bg-surface-1 border border-edge-1 rounded-lg p-3 font-mono text-sm text-txt-2 mb-3">
              <span className="text-zen-purple font-bold">Final Score</span> = (<span className="text-txt-1">Efficiency</span> <span className="text-txt-4">&times;</span> <span className="text-amber-500">0.3</span>) + (<span className="text-txt-1">Contribution</span> <span className="text-txt-4">&times;</span> <span className="text-emerald-500">0.7</span>)
            </div>
            <div className="grid grid-cols-3 gap-3 text-xs text-txt-3">
              <div className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 shrink-0" />
                <span><strong className="text-txt-2">Efficiency</strong> = (Closure% &times; 0.4) + (Prod% &times; 0.6) &mdash; how well do they complete work?</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 shrink-0" />
                <span><strong className="text-txt-2">Contribution</strong> = Staff deployments / Total team deployments (all environments) &mdash; how much of the team's delivery is theirs?</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-zen-purple mt-1.5 shrink-0" />
                <span><strong className="text-txt-2">Why 70/30?</strong> Volume matters more. Doing 91 tickets to production beats doing 3, even if 3/3 is "100%".</span>
              </div>
            </div>
          </div>
          <div className="text-center shrink-0">
            <ScoreRing score={insights.teamAvgScore} size={64} />
            <div className="text-[10px] text-txt-3 mt-1 font-semibold">Team Avg</div>
          </div>
        </div>
      </div>

      {/* ================================================================ */}
      {/* SUMMARY CARDS                                                    */}
      {/* ================================================================ */}
      <div className="grid grid-cols-4 gap-3">
        {/* Top Performer */}
        <div className="bg-surface-2 border border-edge-1 rounded-xl p-4 flex flex-col gap-1 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-txt-3 uppercase tracking-wider">Top Performer</span>
            <span className="text-sm">&#127942;</span>
          </div>
          <div className="text-lg font-bold text-txt-1">{insights.topPerformer?.name?.split(" ")[0] || "\u2014"}</div>
          <span className="text-[11px] text-txt-4">
            {insights.topPerformer ? `Score ${insights.topPerformer.score} \u2014 ${insights.topPerformer.prodShare}% of team's prod output` : "No data"}
          </span>
        </div>

        {/* Most Consistent */}
        <div className="bg-surface-2 border border-edge-1 rounded-xl p-4 flex flex-col gap-1 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-txt-3 uppercase tracking-wider">Most Consistent</span>
            <span className="text-sm">&#128202;</span>
          </div>
          <div className="text-lg font-bold text-txt-1">{insights.mostConsistent?.name?.split(" ")[0] || "\u2014"}</div>
          <span className="text-[11px] text-txt-4">
            {insights.mostConsistent ? `Avg score ${insights.mostConsistent.avgScore} \u2014 lowest variance` : "No data"}
          </span>
        </div>

        {/* Biggest Improver */}
        <div className="bg-surface-2 border border-edge-1 rounded-xl p-4 flex flex-col gap-1 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-txt-3 uppercase tracking-wider">Biggest Improver</span>
            <span className="text-sm">&#128200;</span>
          </div>
          <div className="text-lg font-bold text-txt-1">{insights.biggestImprover?.name?.split(" ")[0] || "\u2014"}</div>
          <span className="text-[11px] text-txt-4">
            {insights.biggestImprover ? `+${insights.biggestImprover.increase} score points week-over-week` : "No data"}
          </span>
        </div>

        {/* Bottleneck */}
        <div className={`bg-surface-2 border rounded-xl p-4 flex flex-col gap-1 shadow-sm ${insights.bottleneck ? "border-red-300" : "border-edge-1"}`}>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-txt-3 uppercase tracking-wider">Bottleneck Alert</span>
            <span className="text-sm">&#9888;&#65039;</span>
          </div>
          <div className={`text-lg font-bold ${insights.bottleneck ? "text-red-500" : "text-emerald-500"}`}>
            {insights.bottleneck?.name?.split(" ")[0] || "None"}
          </div>
          <span className="text-[11px] text-txt-4">
            {insights.bottleneck
              ? `Closure ${insights.bottleneck.closureRate}% but PROD only ${insights.bottleneck.prodRate}% \u2014 stuck in pipeline`
              : "No pipeline bottlenecks"}
          </span>
        </div>
      </div>

      {/* ================================================================ */}
      {/* FILTERS                                                          */}
      {/* ================================================================ */}
      <div className="bg-surface-2 border border-edge-1 rounded-xl p-4 shadow-sm sticky top-0 z-10">
        <div className="flex flex-wrap items-center gap-2.5">
          <MultiSelect label="Staff" options={allStaffNames} selected={staffFilter} onChange={setStaffFilter} allLabel="All Staff" />

          <div className="flex items-center gap-1.5 bg-surface-3 border border-edge-1 rounded-lg px-3 py-1.5">
            <span className="text-[10px] text-txt-4 uppercase tracking-wider">Weeks</span>
            <select value={weeksBack} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setWeeksBack(Number(e.target.value))}
              className="bg-transparent text-xs font-semibold text-txt-2 outline-none cursor-pointer">
              {[2, 4, 6, 8, 12].map((n: number) => <option key={n} value={n}>Last {n}</option>)}
            </select>
          </div>

          <div className="flex bg-surface-3 rounded-lg p-0.5 border border-edge-1">
            {["ALL", ...(data?.projects || [])].map((p: string) => (
              <button key={p} onClick={() => setProjectFilter(p)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all ${
                  projectFilter === p ? "bg-zen-purple text-white shadow-sm" : "text-txt-3 hover:text-txt-2"
                }`}>
                {p === "ALL" ? "All" : p}
              </button>
            ))}
          </div>

          <button onClick={resetFilters}
            className="ml-auto flex items-center gap-1 bg-surface-3 border border-edge-1 rounded-lg px-3 py-1.5 text-xs font-semibold text-txt-3 hover:text-red-400 hover:border-red-300 transition-colors">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M6 18L18 6M6 6l12 12" /></svg>
            Reset
          </button>

          <button onClick={() => exportCSV(filteredRows, weeks)}
            className="flex items-center gap-1 bg-surface-3 border border-edge-1 rounded-lg px-3 py-1.5 text-xs font-semibold text-txt-3 hover:text-zen-purple hover:border-zen-purple transition-colors">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            CSV
          </button>
        </div>
      </div>

      {/* ================================================================ */}
      {/* PRODUCTIVITY SCORE TABLE                                         */}
      {/* ================================================================ */}
      <div className="bg-surface-2 border border-edge-1 rounded-xl p-5 shadow-sm">
        <div className="flex items-end justify-between mb-3">
          <div>
            <h3 className="text-base font-bold text-txt-1">Week-wise Productivity Scores</h3>
            <p className="text-xs text-txt-3 mt-0.5">Score = (Closure% &times; 0.4) + (PROD% &times; 0.6) &mdash; click headers to sort</p>
          </div>
          {loading && <div className="w-4 h-4 border-2 border-edge-2 border-t-zen-purple rounded-full animate-spin" />}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" style={{ minWidth: Math.max(680, 200 + weeks.length * 120 + 200) }}>
            <thead>
              <tr className="border-b border-edge-2">
                <th className="text-left py-2.5 px-3 font-semibold text-txt-3 uppercase text-[10px] tracking-wider sticky left-0 bg-surface-2 z-10 min-w-[160px] cursor-pointer hover:text-zen-purple"
                  onClick={() => handleSort("name")}>
                  Staff {sortCol === "name" ? (sortDir === "asc" ? "\u2191" : "\u2193") : ""}
                </th>
                {weeks.map((w: WeekLabel) => (
                  <th key={w.label} className="text-center py-2.5 px-1 font-semibold text-txt-2 text-[10px] cursor-pointer hover:text-zen-purple min-w-[130px]"
                    onClick={() => handleSort(w.label)}>
                    <div>{weekDateRange(w)}</div>
                    {sortCol === w.label ? <span className="text-zen-purple">{sortDir === "asc" ? "\u2191" : "\u2193"}</span> : null}
                  </th>
                ))}
                <th className="text-center py-2.5 px-2 font-semibold text-txt-3 text-[10px] uppercase cursor-pointer hover:text-zen-purple"
                  onClick={() => handleSort("closureRate")}>
                  <div>Closure% {sortCol === "closureRate" ? (sortDir === "asc" ? "\u2191" : "\u2193") : ""}</div>
                  <div className="text-[8px] text-txt-4 font-normal normal-case">closed / assigned</div>
                </th>
                <th className="text-center py-2.5 px-2 font-semibold text-txt-3 text-[10px] uppercase cursor-pointer hover:text-zen-purple"
                  onClick={() => handleSort("prodRate")}>
                  <div>PROD% {sortCol === "prodRate" ? (sortDir === "asc" ? "\u2191" : "\u2193") : ""}</div>
                  <div className="text-[8px] text-txt-4 font-normal normal-case">prod / closed</div>
                </th>
                <th className="text-center py-2.5 px-2 font-bold text-zen-purple text-[10px] uppercase cursor-pointer hover:text-zen-purple-light"
                  onClick={() => handleSort("score")}>
                  Score {sortCol === "score" ? (sortDir === "asc" ? "\u2191" : "\u2193") : ""}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row: StaffRow) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const sc = scoreColor(row.totals.score);
                const totalLowVol = row.totals.assigned < 3;
                return (
                  <tr key={row.name} className="border-b border-edge-1 hover:bg-surface-3/50 transition-colors">
                    <td className="py-2.5 px-3 sticky left-0 bg-surface-2 z-10">
                      <div className="flex items-center gap-2">
                        <Avatar name={row.name} />
                        <div>
                          <span className="font-semibold text-txt-1 text-[12px]">{row.name}</span>
                          <div className="text-[9px] text-txt-4">
                            {row.totals.assigned} assigned &middot; {row.totals.closed} closed
                            {totalLowVol && <span className="text-amber-400 ml-1">&middot; low volume</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    {weeks.map((w: WeekLabel) => {
                      const d: WeekData = (row.weeks[w.label] || {}) as WeekData;
                      const wsc = scoreColor(d.score || 0);
                      const lowVol = (d.assigned || 0) < 3;
                      const wClosure = d.closureRate || 0;
                      const wProd = d.prodRate || 0;
                      return (
                        <td key={w.label} className={`text-center py-2 px-1 border ${d.score > 0 && !lowVol ? `${wsc.bg} ${wsc.border}` : "border-transparent"}`}
                          style={lowVol && d.score > 0 ? { opacity: 0.5 } : undefined}>
                          <div className={`font-bold text-sm font-mono ${lowVol ? "text-txt-4" : wsc.text}`}>{d.score || 0}</div>
                          <div className="text-[9px] text-txt-4 mt-0.5">
                            {d.assigned || 0} assigned &middot; {d.closed || 0} closed &middot; <span style={{ color: lowVol ? undefined : STAGE_COLORS.PROD }}>{d.PROD || 0} prod</span>
                          </div>
                          <div className="text-[8px] text-txt-4 mt-0.5">
                            <span className="text-amber-500">{wClosure}% closed</span>{" \u00b7 "}<span className="text-emerald-500">{wProd}% prod</span>
                          </div>
                          {lowVol && (d.assigned || 0) > 0 && <div className="text-[8px] text-txt-4 italic">low vol</div>}
                        </td>
                      );
                    })}
                    <td className="text-center py-2.5 px-2">
                      <div className="font-bold text-sm font-mono text-amber-500">{row.totals.closureRate}%</div>
                      <div className="text-[9px] text-txt-4">{row.totals.closed} of {row.totals.assigned}</div>
                    </td>
                    <td className="text-center py-2.5 px-2">
                      <div className="font-bold text-sm font-mono text-emerald-500">{row.totals.prodRate}%</div>
                      <div className="text-[9px] text-txt-4">{row.totals.PROD} of {row.totals.closed}</div>
                    </td>
                    <td className="text-center py-2.5 px-2">
                      {(() => {
                        const cm = contributionMap[row.name] || {} as ContributionEntry;
                        const fsc = scoreColor(cm.finalScore || 0);
                        return (
                          <div className="flex items-center justify-center gap-1.5">
                            <ScoreRing score={cm.finalScore || 0} size={32} />
                            <div>
                              <span className={`text-[10px] font-semibold ${fsc.text}`}>{scoreLabel(cm.finalScore || 0)}</span>
                              <div className="text-[8px] text-txt-4">eff {cm.efficiency || 0} &middot; contrib {cm.contribution || 0}</div>
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })}
              {/* Team total row */}
              <tr className="border-t-2 border-edge-2 bg-surface-3/30">
                <td className="py-3 px-3 font-bold text-txt-1 text-[12px] sticky left-0 bg-surface-2 z-10">
                  Team ({filteredRows.length})
                </td>
                {weeks.map((w: WeekLabel) => {
                  const t: WeekData = (teamTotals[w.label] || {}) as WeekData;
                  const tsc = scoreColor(t.score || 0);
                  return (
                    <td key={w.label} className="text-center py-3 px-1">
                      <div className={`font-bold text-sm font-mono ${tsc.text}`}>{t.score || 0}</div>
                      <div className="text-[9px] text-txt-4">{t.assigned || 0} assigned &middot; {t.closed || 0} closed &middot; {t.PROD || 0} prod</div>
                      <div className="text-[8px] text-txt-4">
                        <span className="text-amber-500">{t.closureRate || 0}% closed</span>{" \u00b7 "}<span className="text-emerald-500">{t.prodRate || 0}% prod</span>
                      </div>
                    </td>
                  );
                })}
                <td className="text-center py-3 font-bold text-amber-500 font-mono">
                  {filteredRows.length > 0 ? Math.round(filteredRows.reduce((s: number, r: StaffRow) => s + r.totals.closureRate, 0) / filteredRows.length) : 0}%
                </td>
                <td className="text-center py-3 font-bold text-emerald-500 font-mono">
                  {filteredRows.length > 0 ? Math.round(filteredRows.reduce((s: number, r: StaffRow) => s + r.totals.prodRate, 0) / filteredRows.length) : 0}%
                </td>
                <td className="text-center py-3">
                  <ScoreRing score={insights.teamAvgScore} size={32} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ================================================================ */}
      {/* ENVIRONMENT STAGE TABLE                                          */}
      {/* ================================================================ */}
      <div className="bg-surface-2 border border-edge-1 rounded-xl p-5 shadow-sm">
        <div className="mb-3">
          <h3 className="text-base font-bold text-txt-1">Team Contribution</h3>
          <p className="text-xs text-txt-3 mt-0.5">Each person's share of total team deployments (Dev + UAT + Prod combined) — e.g. "Dev %" = their Dev items / all deployments across all environments</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[750px]">
            <thead>
              <tr className="border-b border-edge-2">
                <th className="text-left py-2.5 px-3 font-semibold text-txt-3 uppercase text-[10px] tracking-wider min-w-[160px]">Staff</th>
                <th className="text-center py-2.5 px-2 font-semibold text-txt-2 text-[10px] uppercase">Items Touched</th>
                <th className="text-center py-2.5 px-2 font-semibold text-[10px] uppercase" style={{ color: STAGE_COLORS.DEV }}>
                  <div>Dev</div>
                  <div className="text-[8px] text-txt-4 font-normal normal-case">count / team share</div>
                </th>
                <th className="text-center py-2.5 px-2 font-semibold text-[10px] uppercase" style={{ color: STAGE_COLORS.UAT }}>
                  <div>UAT</div>
                  <div className="text-[8px] text-txt-4 font-normal normal-case">count / team share</div>
                </th>
                <th className="text-center py-2.5 px-2 font-semibold text-[10px] uppercase" style={{ color: STAGE_COLORS.PROD }}>
                  <div>Production</div>
                  <div className="text-[8px] text-txt-4 font-normal normal-case">count / team share</div>
                </th>
                <th className="text-center py-2.5 px-2 font-bold text-txt-2 text-[10px] uppercase">
                  <div>Total %</div>
                  <div className="text-[8px] text-txt-4 font-normal normal-case">all env combined</div>
                </th>
                <th className="text-center py-2.5 px-2 font-semibold text-txt-3 text-[10px] uppercase">
                  <div>Efficiency</div>
                  <div className="text-[8px] text-txt-4 font-normal normal-case">personal rate</div>
                </th>
                <th className="text-center py-2.5 px-2 font-bold text-zen-purple text-[10px] uppercase">
                  <div>Final Score</div>
                  <div className="text-[8px] text-txt-4 font-normal normal-case">30% eff + 70% contribution</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row: StaffRow) => {
                const cm = contributionMap[row.name] || {} as ContributionEntry;
                const fsc = scoreColor(cm.finalScore || 0);
                const esc = scoreColor(cm.efficiency || 0);
                return (
                  <tr key={row.name} className="border-b border-edge-1 hover:bg-surface-3/50 transition-colors">
                    <td className="py-2.5 px-3">
                      <div className="flex items-center gap-2">
                        <Avatar name={row.name} />
                        <span className="font-semibold text-txt-1 text-[12px]">{row.name}</span>
                      </div>
                    </td>
                    <td className="text-center py-2.5 font-bold text-sm text-txt-1 font-mono">{row.totals.assigned}</td>
                    <td className="text-center py-2.5">
                      <div className="font-bold text-sm font-mono" style={{ color: STAGE_COLORS.DEV }}>{row.totals.DEV || 0}</div>
                      <div className="text-[9px] font-semibold" style={{ color: STAGE_COLORS.DEV }}>{cm.devShare || 0}%</div>
                    </td>
                    <td className="text-center py-2.5">
                      <div className="font-bold text-sm font-mono" style={{ color: STAGE_COLORS.UAT }}>{row.totals.UAT || 0}</div>
                      <div className="text-[9px] font-semibold" style={{ color: STAGE_COLORS.UAT }}>{cm.uatShare || 0}%</div>
                    </td>
                    <td className="text-center py-2.5">
                      <div className="font-bold text-sm font-mono" style={{ color: STAGE_COLORS.PROD }}>{row.totals.PROD || 0}</div>
                      <div className="text-[9px] font-semibold" style={{ color: STAGE_COLORS.PROD }}>{cm.prodShare || 0}%</div>
                    </td>
                    <td className="text-center py-2.5">
                      <span className="font-bold text-sm font-mono text-txt-1">{cm.totalShare || 0}%</span>
                    </td>
                    <td className="text-center py-2.5">
                      <span className={`inline-flex items-center justify-center min-w-[28px] px-1.5 py-0.5 rounded-md font-bold text-[12px] font-mono ${esc.bg} ${esc.text} ${esc.border} border`}>
                        {cm.efficiency || 0}
                      </span>
                    </td>
                    <td className="text-center py-2.5">
                      <span className={`inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-md font-bold text-sm font-mono ${fsc.bg} ${fsc.text} ${fsc.border} border`}>
                        {cm.finalScore || 0}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {/* Team totals */}
              <tr className="border-t-2 border-edge-2 bg-surface-3/30">
                <td className="py-3 px-3 font-bold text-txt-1 text-[12px]">Team Total</td>
                <td className="text-center py-3 font-bold text-sm text-txt-1 font-mono">{teamEnvTotals.assigned}</td>
                <td className="text-center py-3 font-bold text-sm font-mono" style={{ color: STAGE_COLORS.DEV }}>{teamEnvTotals.DEV}</td>
                <td className="text-center py-3 font-bold text-sm font-mono" style={{ color: STAGE_COLORS.UAT }}>{teamEnvTotals.UAT}</td>
                <td className="text-center py-3 font-bold text-sm font-mono" style={{ color: STAGE_COLORS.PROD }}>{teamEnvTotals.PROD}</td>
                <td className="text-center py-3 font-bold text-sm font-mono text-txt-1">100%</td>
                <td className="text-center py-3 text-txt-4 text-[10px]">&mdash;</td>
                <td className="text-center py-3">
                  <ScoreRing score={insights.teamAvgScore} size={32} />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ================================================================ */}
      {/* HOW TO READ                                                      */}
      {/* ================================================================ */}
      <div className="bg-surface-1 border border-edge-1 rounded-xl p-4">
        <h4 className="text-xs font-bold text-txt-2 mb-2">How to read the scores</h4>
        <div className="grid grid-cols-3 gap-3 text-[11px] text-txt-3">
          <div className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1 shrink-0" />
            <span><strong>Low Closure Rate</strong> &mdash; not finishing assigned work. Could be overloaded, blocked, or unclear requirements.</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1 shrink-0" />
            <span><strong>Low PROD Rate</strong> &mdash; finishing work but it&apos;s stuck downstream. Usually a QA/deployment bottleneck, not the developer.</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1 shrink-0" />
            <span><strong>Both Low</strong> &mdash; that&apos;s the coaching conversation. When Closure is high but PROD is low, investigate the pipeline.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
